import {createParser, EventSourceMessage, ParserCallbacks} from "eventsource-parser";
import {returnFalse, returnTrue} from "../common-functions";
import {asyncExecuteInBackground} from "../async-utils/async-utils";
import {evaluateFunction} from "../evaluable";
import {Values} from "../common-types";

export function eventSourceParserCreate (
    options: ParserCallbacks
) {
    return createParser(options);
}

export namespace EventSourceParser {
    export type Message = EventSourceMessage;
}

export const EventSourceClientRequestMethod = {
    Post: 'POST',
    Get: 'GET'
} as const;

export type EventSourceClientRequestMethod = Values<typeof EventSourceClientRequestMethod>;

export async function eventSourceClientRequest(
    options: {
        url: string;
        method: EventSourceClientRequestMethod;
        headers?: Record<string, string>;
        signal?: AbortSignal;
        credentials?: RequestCredentials;
        data?: unknown;
        onEventSourceData: (data: unknown) => void;
        isEventSourceEndEvent?: (message: EventSourceParser.Message) => boolean;
        isEventSourceDataEvent?: (message: EventSourceParser.Message) => boolean;
    },
) {
    const {
        url,
        method,
        headers,
        data,
        signal,
        credentials,
        onEventSourceData,
        isEventSourceEndEvent = returnFalse,
        isEventSourceDataEvent = returnTrue,
    } = options;

    const res = await fetch(url, {
        method: method,
        signal: signal,
        credentials: credentials,
        ...evaluateFunction(() => {

            switch (method) {
                case EventSourceClientRequestMethod.Get: {
                    return {
                        headers: headers ?? {}
                    };
                }
                case EventSourceClientRequestMethod.Post: {
                    return {
                        headers: {
                            "Content-Type": "application/json",
                            ...(headers ?? {})
                        },
                        body: JSON.stringify(data ?? '')
                    }
                }
            }
        }),
    });

    const body = res.body;

    if (!res.ok || !body) {
        throw new Error(`HTTP ${res.status}`);
    }

    // Feed chunks into the SSE parser
    return new Promise<Response>((resolve, reject) => {

        asyncExecuteInBackground(async () => {
            const reader = body.getReader();       // Web ReadableStream in Node 18+
            const decoder = new TextDecoder();

            const parser = eventSourceParserCreate({
                onError: (error) => {
                    reject(error);
                },
                onEvent: (evt) => {
                    if (isEventSourceEndEvent(evt)) {
                        resolve(res);
                    } else if (isEventSourceDataEvent(evt)) {
                        onEventSourceData(evt.data);
                    } else {
                        // Ignore other events
                    }
                }
            });

            let finished = false;
            while (!finished) {
                const { value, done } = await reader.read();
                if (!done) {
                    parser.feed(decoder.decode(value, { stream: true }));
                } else {
                    finished = true;
                }
            }
        }, {
            onError: reject
        })
    });
}